import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useNec1099 } from '../../hooks/useReports';
import { CONSOLIDATED, useActiveSelection } from '../../stores/useCompanyStore';
import { formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/reports/1099-nec')({
  component: Nec1099Page,
});

function Nec1099Page() {
  const selection = useActiveSelection();
  const companyId = selection === CONSOLIDATED ? null : selection;
  const [year, setYear] = useState(new Date().getFullYear());
  const { data, isLoading, error } = useNec1099(companyId, year);

  if (!selection) return <NoCompany />;
  if (selection === CONSOLIDATED) {
    return (
      <div>
        <Link to="/reports" style={{ fontSize: 13 }}>← All reports</Link>
        <div className="card" style={{ textAlign: 'center', padding: 32, marginTop: 8 }}>
          <h1 style={{ marginTop: 0, fontSize: 20 }}>1099-NEC</h1>
          <p className="muted">
            1099-NEC is filed per legal entity. Pick a single company in the switcher.
          </p>
        </div>
      </div>
    );
  }

  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div>
      <Link to="/reports" style={{ fontSize: 13 }}>← All reports</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>1099-NEC</h1>
        <label style={{ fontSize: 13 }}>
          Tax year{' '}
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load report.</p>}

      {data && (
        <>
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            Nonemployee compensation (Box 1) for {data.year}. Vendors at or above the{' '}
            {formatCurrency(String(data.threshold))} threshold generally require a 1099-NEC.
          </p>

          {data.rows.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 24 }}>
              <p className="muted" style={{ margin: 0 }}>
                No reportable payments to 1099 vendors in {data.year}.
              </p>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Tax ID</th>
                  <th>Classification</th>
                  <th style={{ textAlign: 'right' }}>Box 1 total</th>
                  <th>1099 required</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.contactId}>
                    <td>
                      {r.name}
                      {r.missingTaxId && r.meetsThreshold && (
                        <span className="tag bad" style={{ marginLeft: 8, fontSize: 11 }}>
                          missing TIN
                        </span>
                      )}
                    </td>
                    <td className="muted">{r.taxId ?? '—'}</td>
                    <td className="muted">{r.taxClassification?.replace(/_/g, ' ') ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(r.total)}</td>
                    <td>
                      {r.meetsThreshold ? (
                        <span className="tag ok">required</span>
                      ) : (
                        <span className="tag">below threshold</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  <td colSpan={3}>Total reportable</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.totalReportable)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          )}

          {data.inactiveVendors.length > 0 && (
            <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
              {data.inactiveVendors.length} 1099-flagged vendor
              {data.inactiveVendors.length === 1 ? '' : 's'} had no reportable activity this year:{' '}
              {data.inactiveVendors.map((v) => v.name).join(', ')}.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function NoCompany() {
  return (
    <div className="card" style={{ textAlign: 'center', padding: 32 }}>
      <p className="muted">No active company.</p>
      <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
        Go to Companies
      </Link>
    </div>
  );
}
